import React, { useState, useEffect, useRef } from 'react';
import './Homepage.css';

const measureTStemWidth = (fontStyle) => {
  const canvas = document.createElement('canvas');
  const size = 200;
  canvas.width = size;
  canvas.height = size;
  let ctx;
  try {
    ctx = canvas.getContext('2d');
  } catch (error) {
    return null;
  }
  if (!ctx) return null;
  ctx.font = fontStyle;
  ctx.fillStyle = 'black';
  ctx.fillText('T', 0, size * 0.85);
  const imageData = ctx.getImageData(0, 0, size, size);
  // Scan horizontally near the bottom of T where only the stem exists
  const scanY = Math.round(size * 0.75);
  let first = -1, last = -1;
  for (let x = 0; x < size; x++) {
    const alpha = imageData.data[(scanY * size + x) * 4 + 3];
    if (alpha > 160) {
      if (first === -1) first = x;
      last = x;
    }
  }
  return first === -1 ? null : last - first + 1;
};

const Homepage = ({ onComplete }) => {
  const [isReady, setIsReady] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const dotRef = useRef(null);
  const [expandPos, setExpandPos] = useState({ top: '50%', left: '50%' });
  const [stemWidth, setStemWidth] = useState('0.075em');

  useEffect(() => {
    let rafId, timer1, timer2, timer3;

    const measure = () => {
      const splash = document.querySelector('.splash');
      if (!splash) return;
      const fontSize = parseFloat(getComputedStyle(splash).fontSize);
      const fontFamily = getComputedStyle(splash).fontFamily;
      const px = measureTStemWidth(`400 ${fontSize}px ${fontFamily}`);
      if (px) setStemWidth(`${px}px`);
    };

    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    fontsReady.then(() => {
      // Measure with the real font (still invisible to user)
      measure();

      // Wait one paint frame so the stem-width re-render is committed before
      // we show the splash and start the animation — no visible jump
      rafId = requestAnimationFrame(() => {
        setIsReady(true);

        // 1. Drop animation
        timer1 = setTimeout(() => {
          setIsAnimated(true);

          // 2. Expand circle
          timer2 = setTimeout(() => {
            if (dotRef.current) {
              const rect = dotRef.current.getBoundingClientRect();
              setExpandPos({
                top: `${rect.top + rect.height / 2}px`,
                left: `${rect.left + rect.width / 2}px`,
              });
            }
            setIsExpanded(true);

            // 3. Done — switch to main page
            timer3 = setTimeout(() => {
              if (onComplete) onComplete();
            }, 800);

          }, 800);

        }, 1000);
      });
    });

    const onResize = () => {
      const splash = document.querySelector('.splash');
      if (!splash) return;
      const fs = parseFloat(getComputedStyle(splash).fontSize);
      const ff = getComputedStyle(splash).fontFamily;
      const w = measureTStemWidth(`400 ${fs}px ${ff}`);
      if (w) setStemWidth(`${w}px`);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', onResize);
    };
  }, [onComplete]);

  return (
    <div className="litho-container" style={{ opacity: isReady ? 1 : 0 }}>
      <div className={`splash ${isAnimated ? 'animate' : ''}`}>
        <div className="letter">L</div>
        <div className="i-container">
          <div className={`i-stem ${isAnimated ? 'animate' : ''}`} style={{ width: stemWidth }}></div>
          <div
            ref={dotRef}
            className={`i-dot ${isAnimated ? 'animate' : ''}`}
            style={{ opacity: isExpanded ? 0 : 1 }}
          ></div>
        </div>
        <div className="letter">T</div>
        <div className="letter">H</div>
        <div className="letter">O</div>
      </div>

      {/* Expansion Circle */}
      <div
        className="expansion-circle"
        style={{
          position: 'fixed',
          top: expandPos.top,
          left: expandPos.left,
          width: '20px',
          height: '20px',
          background: '#ff0000',
          borderRadius: '50%',
          transform: `translate(-50%, -50%) scale(${isExpanded ? 350 : 0})`,
          transition: 'transform 800ms cubic-bezier(0.86, 0, 0.07, 1)',
          zIndex: 15,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

export default Homepage;
